import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  User,
  Shield,
  Users,
  Settings,
  DollarSign,
  Scale,
  Megaphone,
  Users2,
} from "lucide-react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const { state, login, clearError } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate("/");
    }
  }, [state.isAuthenticated, navigate]);

  // Clear error when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password);
  };

  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    const roleCredentials: {
      [key: string]: { email: string; password: string };
    } = {
      admin: { email: "admin@syndicate.eg", password: "admin123" },
      board: { email: "board@syndicate.eg", password: "board123" },
      committee: { email: "committee@syndicate.eg", password: "committee123" },
      secretariat: {
        email: "secretariat@syndicate.eg",
        password: "secretariat123",
      },
      member: { email: "member@syndicate.eg", password: "member123" },
    };
    const credentials = roleCredentials[role];
    if (credentials) {
      setEmail(credentials.email);
      setPassword(credentials.password);
    }
  };

  const roleOptions = [
    {
      id: "admin",
      name: "Administrator",
      description: "Full system access",
      icon: Shield,
      color: "bg-red-500",
      email: "admin@syndicate.eg",
      password: "admin123",
    },
    {
      id: "board",
      name: "Board Member",
      description: "Governance and oversight",
      icon: Users,
      color: "bg-blue-500",
      email: "board@syndicate.eg",
      password: "board123",
    },
    {
      id: "committee",
      name: "Committee Chair",
      description: "Committee management",
      icon: Users2,
      color: "bg-green-500",
      email: "committee@syndicate.eg",
      password: "committee123",
    },
    {
      id: "secretariat",
      name: "Secretariat Staff",
      description: "Administrative support",
      icon: Settings,
      color: "bg-purple-500",
      email: "secretariat@syndicate.eg",
      password: "secretariat123",
    },
    {
      id: "member",
      name: "Regular Member",
      description: "Self-service access",
      icon: User,
      color: "bg-orange-500",
      email: "member@syndicate.eg",
      password: "member123",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left side - Login Form */}
        <div className="flex items-center justify-center">
          <Card className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="h-16 w-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">SP</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Syndicate of Pilots
              </h1>
              <p className="text-gray-600">
                Enterprise Resource Planning System
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {state.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{state.error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Demo credentials available below
              </p>
            </div>
          </Card>
        </div>

        {/* Right side - Role Selection */}
        <div className="flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Demo Access
              </h2>
              <p className="text-gray-600">
                Select a role to automatically fill credentials
              </p>
            </div>

            <div className="space-y-4">
              {roleOptions.map((role) => (
                <Card
                  key={role.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedRole === role.id
                      ? "ring-2 ring-primary-500 bg-primary-50"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleRoleSelect(role.id)}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${role.color}`}>
                      <role.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {role.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {role.description}
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        <p>Email: {role.email}</p>
                        <p>Password: {role.password}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">
                System Features
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Membership
                </div>
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-2" />
                  Governance
                </div>
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  Administration
                </div>
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Accounting
                </div>
                <div className="flex items-center">
                  <Scale className="h-4 w-4 mr-2" />
                  Legal Affairs
                </div>
                <div className="flex items-center">
                  <Megaphone className="h-4 w-4 mr-2" />
                  Media
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
